<template>
  <modal ref="modal" :modal-id="modalId" :title="title" :show-on-mount="showOnMount"
         :size="size" :click-outside-to-close="clickOutsideToClose" @closed="closed">
    <template #header>
      <slot name="header" />
    </template>
    <template #sub-heading>
      <slot name="sub-heading" />
    </template>
    <template #body>
      <alert v-if="errorMessage">
        {{ errorMessage }}
      </alert>
      <slot name="body" />
    </template>
    <template #footer>
      <div class="footer-buttons">
        <button type="button" class="btn btn-light" @click="close">
          {{ cancelText }}
        </button>

        <bot-protected-submit-button v-if="useBotProtectionIfAvailable" :form-valid="!submitDisabled"
                                     :label="submitText" :captcha-action="captchaAction" :invisible-captcha="true"
                                     :calculate-solution-on-mount="true" :submit-in-progress="submitting"
                                     @submit="onSubmit" />
        <submit-button v-else :form-valid="!submitDisabled" :label="submitText" :submit-in-progress="submitting"
                       @click="onSubmit" />
      </div>
    </template>
    <bot-protection-disclaimer v-if="botProtectionEnabled" />
  </modal>
</template>

<script>
import Alert from '../components/alert'
import BotProtectedSubmitButton from './buttons/bot-protected-submit-button'
import BotProtectionDisclaimer from './inputs/bot-protection-disclaimer'
import { inject } from 'vue'
import Modal from './modal'
import SubmitButton from './buttons/submit-button'

export default {
  components: { Alert, BotProtectedSubmitButton, BotProtectionDisclaimer, Modal, SubmitButton },

  props: {
    cancelText: {
      type: String,
      required: false,
      default() { return inject('i18n').t('administrative.cancel') }
    },
    modalId: {
      type: String,
      required: true
    },
    submitText: {
      type: String,
      required: false,
      default() { return inject('i18n').t('submit') }
    },
    title: {
      type: String,
      required: false
    },
    showOnMount: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'lg'
    },
    clickOutsideToClose: {
      type: Boolean,
      required: false,
      default: true
    },
    captchaAction: {
      type: String,
      required: false
    },
    submitAction: {
      type: Function,
      required: true
    },
    submitDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    useBotProtectionIfAvailable: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  emits: ['clear'],

  data() {
    return {
      submitting: false,
      errorMessage: ''
    }
  },

  computed: {
    botProtectionEnabled() {
      return this.useBotProtectionIfAvailable && (this.agraConfiguration.botProtection.kind !== 'none')
    }
  },

  methods: {
    clear() {
      this.$emit('clear')
      this.submitting = false
      this.errorMessage = ''
    },
    onSubmit: async function(payload) {
      this.submitting = true

      try {
        await this.submitAction(payload)
        this.close()
      }
      catch (e) {
        this.errorMessage = e.message
        this.submitting = false
      }
    },

    // Handles the case where the modal was closed by user clicking outside it, and the case
    // Cancel button was clicked.
    closed() {
      this.clear()
    },

    // So that consumers can add a ref for this component and have it work on the composed Modal
    open() {
      this.$refs.modal.open()
    },
    close() { // Also used in Cancel button
      this.$refs.modal.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.footer-buttons {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
</style>
